import { create, StateCreator } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { PlayersState } from './types';
import { offlinePlayersApi } from '../../api';

const initialState = {
  offlinePlayers: [],
  boardId: null,
  isLoading: false,
  hasLoaded: false,
  error: null,
};

const createPlayersStore: StateCreator<PlayersState, [['zustand/devtools', never]]> = (set, get) => ({
  ...initialState,

  fetchOfflinePlayers: async (boardId: string) => {
    const state = get();
    // Only show loading spinner on initial load (stale-while-revalidate pattern)
    if (!state.hasLoaded || state.boardId !== boardId) {
      set({ isLoading: true });
    }
    set({ error: null, boardId });

    try {
      const players = await offlinePlayersApi.list(boardId);
      set({ offlinePlayers: players, hasLoaded: true });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to fetch offline players' });
    } finally {
      set({ isLoading: false });
    }
  },

  refreshOfflinePlayers: async () => {
    const { boardId } = get();
    if (!boardId) return;

    // Don't show loading spinner on refresh (stale-while-revalidate)
    set({ error: null });

    try {
      const players = await offlinePlayersApi.list(boardId);
      set({ offlinePlayers: players });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to refresh offline players' });
    }
  },

  clearError: () => set({ error: null }),
  reset: () => set(initialState),
});

export const usePlayersStore = create<PlayersState>()(
  devtools(createPlayersStore, { name: 'players-store' })
);

// Re-export types
export type { PlayersState } from './types';

// Selectors
export const selectOfflinePlayers = (state: PlayersState) => state.offlinePlayers;
export const selectPlayersLoading = (state: PlayersState) => state.isLoading;
export const selectPlayersError = (state: PlayersState) => state.error;
export const selectPlayersBoardId = (state: PlayersState) => state.boardId;
